// BaseThemeService.js

import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

import { collections, db } from 'firebaseConfig';
import {
  normalizeThemePack,
  toResolvableThemePack,
} from './themePackModel';
import { ORBITAL_DEFAULT_PACK, ThemeMode } from 'context/theme';

const BASES_COLLECTION = collections.bases;
const THEME_COLLECTION = 'theme';
const THEME_DOC_KEY = 'main';

function stripUndefinedDeep(value) {
  if (Array.isArray(value)) {
    return value
      .filter((entry) => entry !== undefined)
      .map((entry) => stripUndefinedDeep(entry));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (entryValue === undefined) {
      return accumulator;
    }
    accumulator[key] = stripUndefinedDeep(entryValue);
    return accumulator;
  }, {});
}

function baseThemeDocument(baseKey) {
  return doc(db, BASES_COLLECTION, baseKey, THEME_COLLECTION, THEME_DOC_KEY);
}

/**
 * Normalize a base theme document into pack-like shape.
 *
 * @param {object|null} data
 * @param {string} baseKey
 * @returns {object|null}
 */
export function normalizeBaseTheme(data, baseKey) {
  if (!data || typeof data !== 'object') return null;

  const packKey = `base:${String(baseKey || '').trim()}`;
  return normalizeThemePack({
    ...data,
    key: packKey,
    id: packKey,
    name: String(data.name || 'Base theme').trim() || 'Base theme',
    userKey: data.updatedBy || data.userKey || null,
  });
}

/**
 * Subscribe to a base's live theme fork.
 *
 * @param {string} baseKey
 * @param {(theme: object|null) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void}
 */
export function subscribeToBaseTheme(baseKey, callback, onError) {
  const normalizedBaseKey = String(baseKey || '').trim();

  if (!normalizedBaseKey || typeof callback !== 'function') {
    return () => {};
  }

  return onSnapshot(
    baseThemeDocument(normalizedBaseKey),
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      callback(normalizeBaseTheme({
        id: snapshot.id,
        ...snapshot.data(),
      }, normalizedBaseKey));
    },
    (error) => {
      if (typeof onError === 'function') {
        onError(error);
      } else {
        console.error('[BaseThemeService] subscribe error:', error);
      }
      callback(null);
    },
  );
}

/**
 * Save the base theme fork (admin/owner).
 *
 * @param {{ baseKey: string, userKey: string, pack: object, sourcePackId?: string|null }} options
 * @returns {Promise<object>}
 */
export async function saveBaseTheme({
  baseKey,
  userKey,
  pack,
  sourcePackId = null,
} = {}) {
  const normalizedBaseKey = String(baseKey || '').trim();
  const normalizedUserKey = String(userKey || '').trim();

  if (!normalizedBaseKey) {
    throw new Error('baseKey is required');
  }
  if (!normalizedUserKey) {
    throw new Error('userKey is required');
  }

  const normalized = normalizeThemePack(pack);
  if (!normalized?.modes) {
    throw new Error('pack modes are required');
  }

  const resolvable = toResolvableThemePack(normalized);
  const payload = stripUndefinedDeep({
    key: THEME_DOC_KEY,
    baseKey: normalizedBaseKey,
    name: normalized.name || 'Base theme',
    schemaVersion: normalized.schemaVersion,
    sourcePackId: sourcePackId
      || normalized.sourcePackId
      || null,
    modes: {
      [ThemeMode.DARK]: resolvable.modes[ThemeMode.DARK],
      [ThemeMode.LIGHT]: resolvable.modes[ThemeMode.LIGHT],
    },
    dateModified: serverTimestamp(),
    updatedBy: normalizedUserKey,
  });

  const docRef = baseThemeDocument(normalizedBaseKey);
  const existing = await getDoc(docRef);
  if (!existing.exists()) {
    payload.dateCreated = serverTimestamp();
  }

  await setDoc(docRef, payload, { merge: true });
  return normalizeBaseTheme(payload, normalizedBaseKey);
}

/**
 * Apply (fork) a library pack onto a base as its theme.
 *
 * @param {{ baseKey: string, userKey: string, libraryPack: object }} options
 * @returns {Promise<object>}
 */
export async function applyLibraryPackToBase({
  baseKey,
  userKey,
  libraryPack,
} = {}) {
  const normalized = normalizeThemePack(libraryPack);
  if (!normalized) {
    throw new Error('libraryPack is required');
  }

  return saveBaseTheme({
    baseKey,
    userKey,
    pack: {
      ...normalized,
      name: normalized.name || 'Base theme',
    },
    sourcePackId: normalized.key,
  });
}

/**
 * Build a resolvable pack for ThemeContext (base doc or builtin).
 *
 * @param {object|null} baseTheme
 * @returns {object}
 */
export function resolveBaseThemePack(baseTheme) {
  if (!baseTheme) {
    return ORBITAL_DEFAULT_PACK;
  }
  return toResolvableThemePack(baseTheme);
}

export { THEME_DOC_KEY };
